package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.engagement.entity.StreakStatus
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * StreakBadge displays the user's current play streak with color-coded status.
 *
 * Color tiers:
 * - 30+ days: Gold (Legendary)
 * - 14+ days: Purple (Blazing)
 * - 7+ days: Blue (Hot Streak)
 * - 3+ days: Orange (On Fire)
 * - 1-2 days: Orange (lighter)
 */
@Composable
fun StreakBadge(
    streak: Int,
    compact: Boolean = false,
    onClick: (() -> Unit)? = null
) {
    if (streak <= 0) return

    val (bgColor, textColor) = when {
        streak >= 30 -> "rgba(255, 215, 0, 0.2)" to Colors.Secondary // Gold
        streak >= 14 -> "rgba(156, 39, 176, 0.2)" to rgb(156, 39, 176) // Purple
        streak >= 7 -> "rgba(33, 150, 243, 0.2)" to Colors.Accent // Blue
        else -> "rgba(255, 152, 0, 0.2)" to Colors.Warning // Orange
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(if (compact) 4.px else 6.px)
            backgroundColor(Color(bgColor))
            padding(if (compact) 2.px else 4.px, if (compact) 6.px else 10.px)
            borderRadius(12.px)
            if (onClick != null) {
                cursor("pointer")
            }
        }
        onClick?.let { handler ->
            onClick { handler() }
        }
    }) {
        // Flame emoji
        Span({
            style {
                fontSize(if (compact) 12.px else 14.px)
            }
        }) {
            Text("\uD83D\uDD25") // 🔥
        }

        // Streak number
        Span({
            style {
                fontWeight(700)
                fontSize(if (compact) 11.px else 13.px)
                color(textColor)
            }
        }) {
            Text(streak.toString())
        }

        // Optional "day" label
        if (!compact && streak > 1) {
            Span({
                style {
                    fontSize(10.px)
                    color(textColor)
                    opacity(0.8)
                }
            }) {
                Text("day streak")
            }
        }
    }
}

/**
 * StreakBadgeLarge displays a larger version of the streak badge
 * for use in modals or profile screens.
 */
@Composable
fun StreakBadgeLarge(
    streak: Int,
    showLabel: Boolean = true
) {
    if (streak <= 0) {
        // Show "no streak" state
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                backgroundColor(Color("rgba(158, 158, 158, 0.1)"))
                padding(8.px, 16.px)
                borderRadius(16.px)
            }
        }) {
            Span({ style { fontSize(20.px); opacity(0.5) } }) {
                Text("\uD83D\uDD25") // 🔥
            }
            Span({
                style {
                    fontSize(14.px)
                    color(Colors.Gray500)
                }
            }) {
                Text("No active streak")
            }
        }
        return
    }

    val status = when {
        streak >= 30 -> StreakStatus.LEGENDARY
        streak >= 14 -> StreakStatus.BLAZING
        streak >= 7 -> StreakStatus.HOT
        streak >= 3 -> StreakStatus.ACTIVE
        else -> StreakStatus.STARTED
    }

    val (bgColor, textColor, label) = when (status) {
        StreakStatus.LEGENDARY -> Triple("rgba(255, 215, 0, 0.2)", Colors.Secondary, "Legendary!")
        StreakStatus.BLAZING -> Triple("rgba(156, 39, 176, 0.2)", rgb(156, 39, 176), "Blazing!")
        StreakStatus.HOT -> Triple("rgba(33, 150, 243, 0.2)", Colors.Accent, "Hot Streak!")
        StreakStatus.ACTIVE -> Triple("rgba(255, 152, 0, 0.2)", Colors.Warning, "On Fire!")
        else -> Triple("rgba(255, 152, 0, 0.15)", Colors.Warning, "Keep it up!")
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(8.px)
            backgroundColor(Color(bgColor))
            padding(16.px, 24.px)
            borderRadius(16.px)
        }
    }) {
        // Flame and number row
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Span({
                style {
                    fontSize(28.px)
                }
            }) {
                Text("\uD83D\uDD25") // 🔥
            }
            Span({
                style {
                    fontSize(32.px)
                    fontWeight(700)
                    color(textColor)
                }
            }) {
                Text(streak.toString())
            }
        }

        // Status label
        if (showLabel) {
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(600)
                    color(textColor)
                }
            }) {
                Text(label)
            }

            Span({
                style {
                    fontSize(12.px)
                    color(Colors.Gray600)
                }
            }) {
                Text("day streak")
            }
        }
    }
}

/**
 * Streak saver indicator - shows if user has their weekly streak saver available.
 */
@Composable
fun StreakSaverBadge(
    available: Boolean,
    compact: Boolean = true
) {
    val bgColor = if (available) "rgba(76, 175, 80, 0.1)" else "rgba(158, 158, 158, 0.1)"
    val textColor = if (available) Colors.Success else Colors.Gray500

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            backgroundColor(Color(bgColor))
            padding(2.px, 8.px)
            borderRadius(10.px)
        }
    }) {
        Span({
            style {
                fontSize(if (compact) 10.px else 12.px)
            }
        }) {
            Text(if (available) "\u2764\uFE0F" else "\uD83D\uDDA4") // ❤️ or 🖤
        }

        if (!compact) {
            Span({
                style {
                    fontSize(11.px)
                    color(textColor)
                }
            }) {
                Text(if (available) "Streak Saver Ready" else "Saver Used")
            }
        }
    }
}
